import type { RootState } from '@features/redux/store';
import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { useScrollablePagination } from '@hooks/useScrollablePagination';
import { fetchOrgData, OrgUrls } from '@api/org';
import { Selection } from '../orgNavTypes';
import { Box, Chip, Link as MuiLink, Typography, Pagination } from '@mui/material';
import type { OrgFilterInput } from '@components/OrgFilters';
import { ExportOrgs } from '@components/buttons/ExportOrgs';
import { colors, DataTable, TableCard, TableLink } from '@components/design-system';
import { MobileOrgCard, MobileOrgCardSkeleton } from './MobileOrgCard';
import { typography as typographyTokens, radii } from '@components/design-system/tokens';

const URL_MAP: Record<Selection.Company | Selection.Reseller | Selection.Location, OrgUrls> = {
    [Selection.Company]: OrgUrls.SearchCompanies,
    [Selection.Reseller]: OrgUrls.SearchResellers,
    [Selection.Location]: OrgUrls.SearchLocations,
} as const;

const ID_MAP: Record<Selection.Company | Selection.Reseller | Selection.Location, string> = {
    [Selection.Company]: 'companyId',
    [Selection.Reseller]: 'resellerId',
    [Selection.Location]: 'locationId',
} as const;

const TOTAL_MAP: Record<Selection.Company | Selection.Reseller | Selection.Location, string> = {
    [Selection.Company]: 'totalCompanies',
    [Selection.Reseller]: 'totalResellers',
    [Selection.Location]: 'totalLocations',
} as const;

const TITLE_MAP: Record<Selection.Company | Selection.Reseller | Selection.Location, string> = {
    [Selection.Company]: 'Companies',
    [Selection.Reseller]: 'Resellers',
    [Selection.Location]: 'Locations',
} as const;

interface OrgTableProps {
    orgFilters: {
        companyIds: string;
        resellerIds: string;
        locationIds: string;
    };
    tab: Selection.Company | Selection.Reseller | Selection.Location;
    onFilterChange?: (filters: OrgFilterInput, newTab: Selection) => void;
    isMobile?: boolean;
}

interface LocationSearchResponse {
    locationId: number;
    locationName: string;
    resellerName: string | null;
    resellerId: number | null;
    companyName: string;
    companyId: number;
    inactive: boolean;
    totalLocations: number;
    numSystems: number;
}

interface ResellerSearchResponse {
    resellerId: number;
    resellerName: string;
    companyName: string;
    companyId: number;
    numLocations: number;
    totalResellers: number;
}

interface CompanySearchResponse {
    companyId: number;
    companyName: string;
    numResellers: number;
    numLocations: number;
    totalCompanies: number;
}

const PAGE_SIZE = 12;

export const OrgTable = ({ tab, orgFilters, onFilterChange, isMobile = false }: OrgTableProps) => {
    const {
        IdToken,
        Roles: { role },
    } = useSelector(({ auth }: RootState) => auth);

    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const { scrollContainerRef, scrollToTop } = useScrollablePagination();

    const handlePageChange = useCallback(
        (newPage: number) => {
            setPage(newPage);
            scrollToTop();
        },
        [scrollToTop]
    );

    const fetchFn = useCallback(async () => {
        if (!role.locations.fullAccess && [Selection.Company, Selection.Reseller].includes(tab)) {
            setData([]);
            setTotal(0);
            setLoading(false);
            return;
        }

        if (!role.resellers.fullAccess && Selection.Company === tab) {
            setData([]);
            setTotal(0);
            setLoading(false);
            return;
        }

        setLoading(true);
        const result = await fetchOrgData<any[]>(IdToken!, URL_MAP[tab], {
            pageNum: page - 1,
            pageSize: PAGE_SIZE,
            ...orgFilters,
        });

        setData(result);
        setTotal(result[0]?.[TOTAL_MAP[tab]] || 0);
        setLoading(false);
    }, [IdToken, tab, orgFilters, page, role]);

    useEffect(() => {
        fetchFn();
    }, [fetchFn]);

    // Reset page and clear data when tab or filters change
    useEffect(() => {
        setPage(1);
        setData([]); // Clear old data immediately to prevent stale cards
        setTotal(0);
    }, [tab, orgFilters]);

    const getColumns = () => {
        const handleNumClick = (filters: OrgFilterInput, newTab: Selection) => {
            onFilterChange?.(filters, newTab);
        };

        if (tab === Selection.Company) {
            return [
                {
                    field: 'companyName',
                    headerName: 'Company',
                    flex: 1.5,
                    minWidth: 200,
                    renderCell: ({ row }: { row: CompanySearchResponse }) => (
                        <TableLink to={`/manage/organizations?type=${Selection.Company}&id=${row.companyId}`}>
                            {row.companyName}
                        </TableLink>
                    ),
                },
                {
                    field: 'numResellers',
                    headerName: '# Resellers',
                    flex: 1,
                    minWidth: 120,
                    renderCell: ({ row }: { row: CompanySearchResponse }) => (
                        <MuiLink
                            component='button'
                            sx={{
                                color: colors.neutral[700],
                                textDecoration: 'underline',
                                fontSize: '0.8125rem',
                                cursor: 'pointer',
                            }}
                            onClick={() =>
                                handleNumClick(
                                    { companies: { [row.companyId]: true }, resellers: {}, locations: {} },
                                    Selection.Reseller
                                )
                            }>
                            {row.numResellers}
                        </MuiLink>
                    ),
                },
                {
                    field: 'numLocations',
                    headerName: '# Locations',
                    flex: 1,
                    minWidth: 120,
                    renderCell: ({ row }: { row: CompanySearchResponse }) => (
                        <MuiLink
                            component='button'
                            sx={{
                                color: colors.neutral[700],
                                textDecoration: 'underline',
                                fontSize: '0.8125rem',
                                cursor: 'pointer',
                            }}
                            onClick={() =>
                                handleNumClick(
                                    { companies: { [row.companyId]: true }, resellers: {}, locations: {} },
                                    Selection.Location
                                )
                            }>
                            {row.numLocations}
                        </MuiLink>
                    ),
                },
            ];
        }

        if (tab === Selection.Reseller) {
            return [
                {
                    field: 'resellerName',
                    headerName: 'Reseller',
                    flex: 1.5,
                    minWidth: 200,
                    renderCell: ({ row }: { row: ResellerSearchResponse }) => (
                        <TableLink to={`/manage/organizations?type=${Selection.Reseller}&id=${row.resellerId}`}>
                            {row.resellerName}
                        </TableLink>
                    ),
                },
                {
                    field: 'companyName',
                    headerName: 'Company',
                    flex: 1.5,
                    minWidth: 200,
                    renderCell: ({ row }: { row: ResellerSearchResponse }) => (
                        <TableLink to={`/manage/organizations?type=${Selection.Company}&id=${row.companyId}`}>
                            {row.companyName}
                        </TableLink>
                    ),
                },
                {
                    field: 'numLocations',
                    headerName: '# Locations',
                    flex: 1,
                    minWidth: 120,
                    renderCell: ({ row }: { row: ResellerSearchResponse }) => (
                        <MuiLink
                            component='button'
                            sx={{
                                color: colors.neutral[700],
                                textDecoration: 'underline',
                                fontSize: '0.8125rem',
                                cursor: 'pointer',
                            }}
                            onClick={() =>
                                handleNumClick(
                                    {
                                        companies: { [row.companyId]: true },
                                        resellers: { [row.resellerId]: true },
                                        locations: {},
                                    },
                                    Selection.Location
                                )
                            }>
                            {row.numLocations}
                        </MuiLink>
                    ),
                },
            ];
        }

        // Location
        return [
            {
                field: 'locationId',
                headerName: 'Location ID',
                flex: 0.8,
                minWidth: 100,
                renderCell: ({ row }: { row: LocationSearchResponse }) => (
                    <TableLink to={`/manage/organizations?type=${Selection.Location}&id=${row.locationId}`}>
                        {row.locationId}
                    </TableLink>
                ),
            },
            {
                field: 'locationName',
                headerName: 'Location',
                flex: 1.5,
                minWidth: 200,
                renderCell: ({ row }: { row: LocationSearchResponse }) => (
                    <TableLink to={`/manage/organizations?type=${Selection.Location}&id=${row.locationId}`}>
                        {row.locationName}
                    </TableLink>
                ),
            },
            {
                field: 'resellerName',
                headerName: 'Reseller',
                flex: 1.2,
                minWidth: 150,
                renderCell: ({ row }: { row: LocationSearchResponse }) =>
                    row.resellerId ? (
                        <TableLink to={`/manage/organizations?type=${Selection.Reseller}&id=${row.resellerId}`}>
                            {row.resellerName}
                        </TableLink>
                    ) : (
                        <Typography sx={{ color: colors.neutral[400], fontSize: '0.8125rem' }}>-</Typography>
                    ),
            },
            {
                field: 'companyName',
                headerName: 'Company',
                flex: 1.2,
                minWidth: 150,
                renderCell: ({ row }: { row: LocationSearchResponse }) => (
                    <TableLink to={`/manage/organizations?type=${Selection.Company}&id=${row.companyId}`}>
                        {row.companyName}
                    </TableLink>
                ),
            },
            {
                field: 'numSystems',
                headerName: '# Systems',
                flex: 0.8,
                minWidth: 100,
                renderCell: ({ row }: { row: LocationSearchResponse }) => (
                    <MuiLink
                        component='button'
                        sx={{
                            color: colors.neutral[700],
                            textDecoration: 'underline',
                            fontSize: '0.8125rem',
                            cursor: 'pointer',
                        }}
                        onClick={() =>
                            handleNumClick(
                                {
                                    companies: { [row.companyId]: true },
                                    resellers: row.resellerId ? { [row.resellerId]: true } : {},
                                    locations: { [row.locationId]: true },
                                },
                                Selection.System
                            )
                        }>
                        {row.numSystems}
                    </MuiLink>
                ),
            },
            {
                field: 'inactive',
                headerName: 'Status',
                flex: 0.6,
                minWidth: 100,
                renderCell: ({ row }: { row: LocationSearchResponse }) => (
                    <Chip
                        size='small'
                        label={row.inactive ? 'Archived' : 'Active'}
                        sx={{
                            backgroundColor: row.inactive ? colors.warning.light : colors.success.light,
                            color: row.inactive ? colors.warning.dark : colors.success.dark,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                        }}
                    />
                ),
            },
        ];
    };

    const startItem = (page - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(page * PAGE_SIZE, total);
    const infoText =
        total > 0
            ? `Showing ${startItem}-${endItem} of ${total} ${TITLE_MAP[tab].toLowerCase()}`
            : `No ${TITLE_MAP[tab].toLowerCase()} found`;

    const totalPages = Math.ceil(total / PAGE_SIZE);

    // Filter out any stale data that doesn't match the current tab's ID field
    const validData = data.filter((item: any) => item[ID_MAP[tab]] !== undefined);

    // Mobile card view
    if (isMobile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    backgroundColor: colors.neutral[100],
                    overflow: 'hidden',
                }}>
                {/* Header */}
                <Box
                    sx={{
                        px: 2,
                        pt: 2,
                        pb: 1,
                        backgroundColor: colors.neutral[0],
                        borderBottom: `1px solid ${colors.neutral[200]}`,
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: colors.neutral[600],
                            }}>
                            {infoText}
                        </Typography>
                        <ExportOrgs params={orgFilters} type={tab} />
                    </Box>
                </Box>

                {/* Cards List */}
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        px: 2,
                        py: 2,
                    }}>
                    {loading ? (
                        // Loading skeletons
                        Array.from({ length: 4 }).map((_, index) => <MobileOrgCardSkeleton key={index} />)
                    ) : validData.length > 0 ? (
                        validData.map((item: any) => (
                            <MobileOrgCard
                                key={`${item[ID_MAP[tab]]}-${tab}`}
                                data={item}
                                tab={tab}
                                onFilterChange={onFilterChange}
                            />
                        ))
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 8,
                                color: colors.neutral[500],
                            }}>
                            <Typography>No {TITLE_MAP[tab].toLowerCase()} found</Typography>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderTop: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, newPage) => handlePageChange(newPage)}
                            color='primary'
                            size='small'
                            siblingCount={0}
                            boundaryCount={1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    borderRadius: `${radii.md}px`,
                                    minWidth: 32,
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        );
    }

    // Desktop table view
    return (
        <Box
            display='flex'
            flexDirection='column'
            flex={1}
            sx={{
                minWidth: 0,
                minHeight: 0,
                height: '100%',
                backgroundColor: colors.neutral[100],
                p: 2,
            }}>
            <TableCard
                title={TITLE_MAP[tab]}
                actions={<ExportOrgs params={orgFilters} type={tab} />}
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <DataTable
                    rows={validData}
                    columns={getColumns() as any}
                    getRowId={(row: any) => `${row[ID_MAP[tab]]}-${tab}`}
                    totalRows={total}
                    page={page}
                    pageSize={PAGE_SIZE}
                    onPageChange={handlePageChange}
                    loading={loading}
                    height='100%'
                    infoText={infoText}
                />
            </TableCard>
        </Box>
    );
};
